// Design System Avatar Component
// User/entity avatar with fallback initials

import React from 'react';
import { Avatar as MuiAvatar, AvatarGroup as MuiAvatarGroup, styled } from '@mui/material';
import PropTypes from 'prop-types';
import { colorTokens, spacingTokens, borderTokens } from '../../tokens';

// Get initials from name
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.split(' ').filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// Get background color based on name
const stringToColor = (string) => {
  let hash = 0;
  for (let i = 0; i < string.length; i++) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  const colors = [
    '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FECA57',
    '#DDA0DD', '#98D8C8', '#FDA7DF', '#4B7BEC', '#A55EEA',
  ];
  
  return colors[Math.abs(hash) % colors.length];
};

// Styled Avatar
const StyledAvatar = styled(MuiAvatar)(({ theme, size = 'medium' }) => {
  const sizeMap = {
    small: 32,
    medium: 40,
    large: 56,
  };
  
  return {
    width: sizeMap[size],
    height: sizeMap[size],
    fontSize: size === 'small' ? '0.875rem' : size === 'large' ? '1.5rem' : '1.125rem',
    fontWeight: 500,
    borderRadius: borderTokens.full,
  };
});

/**
 * Avatar - User/entity avatar component
 * 
 * @example
 * // With image
 * <Avatar src="/user-photo.jpg" alt="John Doe" />
 * 
 * // With name (shows initials)
 * <Avatar name="John Doe" />
 * 
 * // Custom size
 * <Avatar name="Jane Smith" size="large" />
 */
export const Avatar = ({
  src,
  alt,
  name,
  size = 'medium',
  variant = 'circular',
  sx,
  ...props
}) => {
  const backgroundColor = !src && name ? stringToColor(name) : undefined;
  
  return (
    <StyledAvatar
      src={src}
      alt={alt || name}
      size={size}
      variant={variant}
      sx={{
        backgroundColor,
        ...sx,
      }}
      {...props}
    >
      {!src && name && getInitials(name)}
    </StyledAvatar>
  );
};

Avatar.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
  name: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  variant: PropTypes.oneOf(['circular', 'rounded', 'square']),
  sx: PropTypes.object,
};

/**
 * AvatarGroup - Group of avatars with overlap
 * 
 * @example
 * <AvatarGroup max={4}>
 *   <Avatar name="John Doe" />
 *   <Avatar name="Jane Smith" />
 *   <Avatar name="Bob Johnson" />
 *   <Avatar name="Alice Brown" />
 *   <Avatar name="Charlie Wilson" />
 * </AvatarGroup>
 */
export const AvatarGroup = styled(MuiAvatarGroup)(({ theme }) => ({
  '& .MuiAvatar-root': {
    borderWidth: 2,
    borderColor: theme.palette.background.paper,
  },
}));

export default Avatar;